import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useInventory } from '../../contexts/InventoryContext';
import { useB2BReservations } from '../../hooks/useB2BReservations';
import { useB2BCart } from '../../hooks/useB2BCart';
import { supabase } from '../../lib/supabase';
import { ensureDefaultLocations, fetchLocationQuantitiesForProducts } from '../../utils/locationInventory';
import { sendB2BReservationConfirmation, type EmailAttachment } from '../../lib/b2bEmail';
import { createProFormaInvoice } from '../../lib/proformaInvoice';
import {
  generateProFormaInvoiceHTML,
  htmlToPdfBase64,
  buildEpcQrDataUrl,
  type SupplierProfile,
} from '../../lib/pdfGenerator';
import { BuyerProfileSummary } from './BuyerProfileSummary';
import { B2BReservationConfirmation } from './B2BReservationConfirmation';
import { B2BReservationHistory } from './B2BReservationHistory';
import { B2BInvoiceHistory } from './B2BInvoiceHistory';
import { B2BPasswordChange } from './B2BPasswordChange';
import { B2BAdminInvoiceManager } from './B2BAdminInvoiceManager';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Search, ShoppingCart, Package, Calendar, Minus, Plus, Trash2,
  Store, History, Settings, LogOut, X, Filter, Menu, ChevronDown, FileText,
  List, LayoutGrid, Rows3
} from 'lucide-react';
import type { InventoryItem, Reservation } from '../../types';
import type { B2BCartItem } from '../../types/b2b';
import { filterCcVsPrimary } from '../../utils/emailRecipients';
import {
  buildProductNameGroups,
  type ProductNameGroup,
  type ProductColor,
  type ProductSize,
} from '../../utils/productGrouping';
import { ProductGroupCard, type ProductGroupDensity } from '../inhouse/product-group-card';
import { ProductVariantMatrixModal, type VariantAddSelection } from '../inhouse/product-variant-matrix-modal';
import { SegmentedControl } from '../inhouse/ui/segmented-control';

interface ColorSize {
  id: string;
  name: string;
  hex_code?: string;
  sort_order?: number;
}

interface LocationInfo {
  id: string;
  name: string;
}

type ActiveView = 'shop' | 'history' | 'invoices' | 'settings';

export const B2BShopPage: React.FC = () => {
  const { buyer, logout } = useAuth();
  const { allItems, fetchAllItems, allItemsLoading } = useInventory();
  const { reservedQuantities, createReservation, getReservedQuantityForProduct } = useB2BReservations();
  const { loadPersisted, persist, clearPersisted } = useB2BCart();
  const hydratedRef = useRef(false);

  const [activeView, setActiveView] = useState<ActiveView>('shop');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [cart, setCart] = useState<B2BCartItem[]>([]);
  const [pickupDate, setPickupDate] = useState('');
  const [colors, setColors] = useState<ColorSize[]>([]);
  const [sizes, setSizes] = useState<ColorSize[]>([]);
  const [locations, setLocations] = useState<LocationInfo[]>([]);
  const [warehouseLocation, setWarehouseLocation] = useState<LocationInfo | null>(null);
  const [productLocationQtys, setProductLocationQtys] = useState<Map<string, { locationId: string; locationName: string; quantity: number }[]>>(new Map());
  const [reserving, setReserving] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{ reservation: Reservation; pickupDate: string } | null>(null);
  const [bgError, setBgError] = useState<string | null>(null);
  const [showCart, setShowCart] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [browseView, setBrowseView] = useState<'grouped' | 'all'>(() =>
    (localStorage.getItem('b2b-browse-view') as 'grouped' | 'all') || 'grouped'
  );
  const [browseDensity, setBrowseDensity] = useState<ProductGroupDensity>(() =>
    (localStorage.getItem('b2b-browse-density') as ProductGroupDensity) || 'list'
  );
  const [selectedProductGroup, setSelectedProductGroup] = useState<ProductNameGroup | null>(null);

  const discount = buyer?.account_discount || 1;
  const vatPct = buyer?.vat_percentage ?? 21;
  const currency = buyer?.preferred_currency || 'EUR';

  // Minimum pickup date: 3 days from now
  const minPickupDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    if (buyer) {
      fetchAllItems(true);
      loadColorsAndSizes();
      loadLocations();
    }
  }, [buyer]);

  useEffect(() => {
    localStorage.setItem('b2b-browse-view', browseView);
  }, [browseView]);

  useEffect(() => {
    localStorage.setItem('b2b-browse-density', browseDensity);
  }, [browseDensity]);

  const loadColorsAndSizes = async () => {
    if (!buyer) return;
    const [colorsRes, sizesRes] = await Promise.all([
      supabase.from('product_colors').select('id, name, hex_code').eq('admin_id', buyer.admin_id).order('name'),
      supabase.from('product_sizes').select('id, name, sort_order').eq('admin_id', buyer.admin_id).order('sort_order'),
    ]);
    if (colorsRes.data) setColors(colorsRes.data);
    if (sizesRes.data) setSizes(sizesRes.data);
  };

  const loadLocations = async () => {
    if (!buyer) return;
    const locs = await ensureDefaultLocations(buyer.admin_id);
    setLocations(locs);
    const wh = locs.find(l => l.name.toLowerCase().includes('warehouse') || l.name.toLowerCase().includes('wearhouse'));
    setWarehouseLocation(wh || null);
  };

  useEffect(() => {
    const loadLocationQtys = async () => {
      if (!buyer || allItems.length === 0) return;
      const productIds = allItems.map(i => i.id);
      const qtys = await fetchLocationQuantitiesForProducts(productIds, buyer.admin_id);
      setProductLocationQtys(qtys);
    };
    loadLocationQtys();
  }, [allItems, buyer]);

  const getWarehouseQty = useCallback((productId: string): number => {
    if (!warehouseLocation) return 0;
    const locs = productLocationQtys.get(productId);
    if (!locs) return 0;
    const wh = locs.find(l => l.locationId === warehouseLocation.id);
    return wh?.quantity || 0;
  }, [productLocationQtys, warehouseLocation]);

  // A product is "in the warehouse catalog" if it has any product_location_inventory
  // row at the warehouse location, regardless of quantity. Items that hit 0 (or are
  // fully reserved) stay visible as Out of stock instead of disappearing.
  const isAtWarehouse = useCallback((productId: string): boolean => {
    if (!warehouseLocation) return false;
    const locs = productLocationQtys.get(productId);
    return !!locs?.some(l => l.locationId === warehouseLocation.id);
  }, [productLocationQtys, warehouseLocation]);

  const getAvailableQty = useCallback((productId: string): number => {
    const whQty = getWarehouseQty(productId);
    const reserved = getReservedQuantityForProduct(productId);
    return Math.max(0, whQty - reserved);
  }, [getWarehouseQty, getReservedQuantityForProduct]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach(item => { if (item.category) cats.add(item.category); });
    return Array.from(cats).sort();
  }, [allItems]);

  const filteredProducts = useMemo(() => {
    return allItems.filter(item => {
      if (!item.sell_price || item.sell_price <= 0) return false;
      if (!isAtWarehouse(item.id)) return false;

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = item.name?.toLowerCase().includes(q) ||
          item.sku?.toLowerCase().includes(q) ||
          item.reference_code?.toLowerCase().includes(q) ||
          item.barcode?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (colorFilter && item.color_id !== colorFilter) return false;
      if (sizeFilter && item.size_id !== sizeFilter) return false;
      return true;
    });
  }, [allItems, searchQuery, categoryFilter, colorFilter, sizeFilter, isAtWarehouse]);

  const formatPrice = (amount: number) => new Intl.NumberFormat('en-EU', { style: 'currency', currency }).format(amount);

  const getColorName = (colorId?: string) => colors.find(c => c.id === colorId)?.name || '';
  const getSizeName = (sizeId?: string) => sizes.find(s => s.id === sizeId)?.name || '';

  // Grouping inputs in the shapes the shared product-grouping utils expect.
  const colorsForGrouping = useMemo<ProductColor[]>(
    () => colors.map(c => ({ id: c.id, name: c.name, hex_code: c.hex_code })),
    [colors]
  );
  const sizesForGrouping = useMemo<ProductSize[]>(
    () => sizes.map((s, idx) => ({ id: s.id, name: s.name, sort_order: s.sort_order ?? idx })),
    [sizes]
  );

  // Build groups from a buyer-discounted projection so the group cards / matrix display
  // the buyer's discounted price. The matrix only returns productId + quantity, so the
  // real (undiscounted) item is re-resolved in addVariantsToCart for cart math.
  const displayItems = useMemo<InventoryItem[]>(
    () => filteredProducts.map(i => ({ ...i, sell_price: (i.sell_price || 0) / discount })),
    [filteredProducts, discount]
  );

  const productGroups = useMemo<ProductNameGroup[]>(
    () => buildProductNameGroups(displayItems, colorsForGrouping, sizesForGrouping, getAvailableQty),
    [displayItems, colorsForGrouping, sizesForGrouping, getAvailableQty]
  );

  const getCartQuantityForProduct = useCallback(
    (productId: string): number =>
      cart.reduce((sum, c) => (c.id === productId ? sum + c.cartQuantity : sum), 0),
    [cart]
  );

  // Available for the matrix: warehouse stock minus reserved minus what's already in the cart.
  const getMatrixAvailableQty = useCallback(
    (productId: string): number =>
      Math.max(0, getAvailableQty(productId) - getCartQuantityForProduct(productId)),
    [getAvailableQty, getCartQuantityForProduct]
  );

  // Reset hydration flag on buyer change so a different login rehydrates its own cart.
  useEffect(() => {
    hydratedRef.current = false;
  }, [buyer?.id]);

  // Hydrate cart from localStorage + server once inventory and warehouse data
  // are ready, re-validating each line against current price/availability.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!buyer) return;
    if (allItemsLoading) return;
    if (allItems.length === 0) return;
    if (!warehouseLocation) return;
    if (productLocationQtys.size === 0) return;

    let cancelled = false;
    (async () => {
      const saved = await loadPersisted();
      if (cancelled) return;
      hydratedRef.current = true;
      if (!saved) return;

      const itemById = new Map(allItems.map(i => [i.id, i]));
      const revalidated: B2BCartItem[] = [];
      let droppedCount = 0;

      for (const line of saved.items) {
        const product = itemById.get(line.id);
        if (!product || !product.sell_price || product.sell_price <= 0) {
          droppedCount++;
          continue;
        }
        // Drop the line only if the product is no longer stocked at the warehouse
        // at all. If it's at the warehouse but currently OOS, drop the line as well
        // (a buyer can't checkout zero qty); the catalog will still show it as OOS.
        if (!isAtWarehouse(product.id)) {
          droppedCount++;
          continue;
        }
        const available = getAvailableQty(product.id);
        if (available <= 0) {
          droppedCount++;
          continue;
        }
        const cappedQty = Math.max(1, Math.min(line.cartQuantity, available));
        const discountedPrice = (product.sell_price || 0) / discount;
        revalidated.push({
          id: product.id,
          name: product.name,
          sku: product.sku,
          category: product.category,
          reference_code: product.reference_code,
          image_url: product.image_url,
          color_id: product.color_id,
          size_id: product.size_id,
          color_name: getColorName(product.color_id) || undefined,
          size_name: getSizeName(product.size_id) || undefined,
          sell_price: product.sell_price || 0,
          discounted_price: discountedPrice,
          quantity: available,
          available_quantity: available,
          cartQuantity: cappedQty,
          lineTotal: cappedQty * discountedPrice,
        });
      }

      if (revalidated.length > 0) setCart(revalidated);
      if (saved.pickupDate && saved.pickupDate >= minPickupDate) {
        setPickupDate(saved.pickupDate);
      }
      if (droppedCount > 0) {
        setBgError(
          `${droppedCount} item${droppedCount === 1 ? ' was' : 's were'} removed from your cart because they are no longer available.`
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    buyer,
    allItems,
    allItemsLoading,
    productLocationQtys,
    warehouseLocation,
    getAvailableQty,
    isAtWarehouse,
    discount,
    minPickupDate,
    loadPersisted,
  ]);

  // Persist cart + pickup date after hydration. Clears storage when both are empty.
  useEffect(() => {
    if (!buyer) return;
    if (!hydratedRef.current) return;
    if (cart.length === 0 && !pickupDate) {
      void clearPersisted();
      return;
    }
    persist(cart, pickupDate);
  }, [buyer, cart, pickupDate, persist, clearPersisted]);

  const addToCart = (item: InventoryItem) => {
    const available = getAvailableQty(item.id);
    const existingIdx = cart.findIndex(c => c.id === item.id);
    if (existingIdx >= 0) {
      const existing = cart[existingIdx];
      if (existing.cartQuantity >= available) return;
      const updated = [...cart];
      const newQty = existing.cartQuantity + 1;
      const discountedPrice = (item.sell_price || 0) / discount;
      updated[existingIdx] = { ...existing, cartQuantity: newQty, lineTotal: newQty * discountedPrice };
      setCart(updated);
    } else {
      if (available <= 0) return;
      const discountedPrice = (item.sell_price || 0) / discount;
      setCart([...cart, {
        id: item.id,
        name: item.name,
        sku: item.sku,
        category: item.category,
        reference_code: item.reference_code,
        image_url: item.image_url,
        color_id: item.color_id,
        size_id: item.size_id,
        color_name: getColorName(item.color_id) || undefined,
        size_name: getSizeName(item.size_id) || undefined,
        sell_price: item.sell_price || 0,
        discounted_price: discountedPrice,
        quantity: available,
        available_quantity: available,
        cartQuantity: 1,
        lineTotal: discountedPrice,
      }]);
    }
  };

  // Bulk add from the variant matrix. Mirrors addToCart rules (real item, discounted price,
  // availability clamp) but merges many { productId, quantity } selections in one pass.
  const addVariantsToCart = (selections: VariantAddSelection[]) => {
    if (selections.length === 0) return;
    setCart(prev => {
      const next = [...prev];
      for (const { productId, quantity } of selections) {
        if (quantity <= 0) continue;
        const item = allItems.find(i => i.id === productId);
        if (!item) continue;
        const available = getAvailableQty(productId);
        if (available <= 0) continue;
        const discountedPrice = (item.sell_price || 0) / discount;
        const existingIdx = next.findIndex(c => c.id === productId);
        if (existingIdx >= 0) {
          const existing = next[existingIdx];
          const newQty = Math.min(existing.cartQuantity + quantity, available);
          next[existingIdx] = { ...existing, cartQuantity: newQty, available_quantity: available, lineTotal: newQty * discountedPrice };
        } else {
          const newQty = Math.min(quantity, available);
          next.push({
            id: item.id,
            name: item.name,
            sku: item.sku,
            category: item.category,
            reference_code: item.reference_code,
            image_url: item.image_url,
            color_id: item.color_id,
            size_id: item.size_id,
            color_name: getColorName(item.color_id) || undefined,
            size_name: getSizeName(item.size_id) || undefined,
            sell_price: item.sell_price || 0,
            discounted_price: discountedPrice,
            quantity: available,
            available_quantity: available,
            cartQuantity: newQty,
            lineTotal: newQty * discountedPrice,
          });
        }
      }
      return next;
    });
  };

  const updateCartQty = (itemId: string, qty: number) => {
    if (qty <= 0) {
      setCart(cart.filter(c => c.id !== itemId));
      return;
    }
    setCart(cart.map(c => {
      if (c.id !== itemId) return c;
      const cappedQty = Math.min(qty, c.available_quantity);
      return { ...c, cartQuantity: cappedQty, lineTotal: cappedQty * c.discounted_price };
    }));
  };

  const removeFromCart = (itemId: string) => setCart(cart.filter(c => c.id !== itemId));

  const cartTotal = useMemo(() => cart.reduce((sum, c) => sum + c.lineTotal, 0), [cart]);
  const cartTaxAmount = useMemo(() => cartTotal * (vatPct / 100), [cartTotal, vatPct]);
  const cartTotalIncVat = useMemo(() => cartTotal + cartTaxAmount, [cartTotal, cartTaxAmount]);
  const cartItemsCount = useMemo(() => cart.reduce((sum, c) => sum + c.cartQuantity, 0), [cart]);

  const handleReserve = async () => {
    if (!buyer || cart.length === 0 || !pickupDate) return;
    if (new Date(pickupDate) < new Date(minPickupDate)) {
      alert('Pickup date must be at least 3 days from today.');
      return;
    }

    setBgError(null);
    setReserving(true);
    try {
      const result = await createReservation({
        pickup_scheduled_date: new Date(pickupDate).toISOString(),
        items: cart.map(c => ({
          product_id: c.id,
          quantity: c.cartQuantity,
          unit_price: c.discounted_price,
          total_price: c.lineTotal,
          source_location_id: warehouseLocation?.id,
        })),
        subtotal: cartTotal,
        tax_amount: cartTaxAmount,
        total_amount: cartTotalIncVat,
      });

      if (!result.success || !result.reservation) {
        alert(result.error || 'Failed to create reservation.');
        setReserving(false);
        return;
      }

      // Snapshot values needed by the background task before clearing UI state.
      const reservation = result.reservation;
      const snapshotCart = cart.map(c => ({
        name: c.name,
        sku: c.sku,
        color_name: c.color_name,
        size_name: c.size_name,
        quantity: c.cartQuantity,
        unit_price: c.discounted_price,
        total_price: c.lineTotal,
        product_id: c.id,
        base_unit_price: c.sell_price,
      }));
      const snapshotPickupDate = pickupDate;
      const snapshotTotals = {
        subtotal: cartTotal,
        tax: cartTaxAmount,
        total: cartTotalIncVat,
        vatPct,
      };

      // Show confirmation immediately; everything below runs in the background.
      setConfirmationData({ reservation, pickupDate: snapshotPickupDate });
      setCart([]);
      setPickupDate('');
      setShowCart(false);
      setReserving(false);
      // Clear persisted draft immediately so the debounced write can't race the reset.
      void clearPersisted();

      // Fire-and-forget: pro-forma invoice, PDFs, confirmation email.
      void (async () => {
        try {
          const { data: adminUser } = await supabase
            .from('users')
            .select('company_name, legal_entity_name, company_street, company_house_number, company_postal_code, company_city, company_country, company_address, company_phone, company_email, company_website, kvk_number, vat_number, company_tax_id, iban, bic, bank_name, logo_url, payment_terms_days, reservation_pdf_cc_emails')
            .eq('id', buyer.admin_id)
            .single();

          const rawPdfCc = Array.isArray(adminUser?.reservation_pdf_cc_emails)
            ? (adminUser!.reservation_pdf_cc_emails as unknown[]).filter(
                (e): e is string => typeof e === 'string' && e.trim().length > 0,
              )
            : [];
          const pdfCcEmails = filterCcVsPrimary(rawPdfCc, buyer.email);

          const supplier: SupplierProfile = {
            company_name: adminUser?.company_name,
            legal_entity_name: adminUser?.legal_entity_name,
            company_street: adminUser?.company_street,
            company_house_number: adminUser?.company_house_number,
            company_postal_code: adminUser?.company_postal_code,
            company_city: adminUser?.company_city,
            company_country: adminUser?.company_country,
            company_address: adminUser?.company_address,
            company_phone: adminUser?.company_phone,
            company_email: adminUser?.company_email,
            company_website: adminUser?.company_website,
            kvk_number: adminUser?.kvk_number,
            vat_number: adminUser?.vat_number,
            company_tax_id: adminUser?.company_tax_id,
            iban: adminUser?.iban,
            bic: adminUser?.bic,
            bank_name: adminUser?.bank_name,
            logo_url: adminUser?.logo_url,
            payment_terms_days: adminUser?.payment_terms_days,
          };

          const attachments: EmailAttachment[] = [];

          // Reservation details are already rendered in the email HTML body
          // (see src/lib/b2bEmail.ts). Skipping a reservation-PDF attachment
          // keeps the email payload small enough that the SMTP edge function
          // never times out.

          try {
            const proFormaResult = await createProFormaInvoice(
              reservation.id,
              reservation.reservation_number,
              buyer.admin_id,
              buyer, snapshotCart, snapshotTotals.subtotal, snapshotTotals.tax, snapshotTotals.total, snapshotTotals.vatPct
            );

            if (proFormaResult.success && proFormaResult.invoice) {
              // Resolve product/color/size from the live products table BEFORE rendering
              // the PDF, so the attached invoice always shows real qty/name/color/size
              // even if the cart snapshot had any undefined fields. Mirrors the working
              // B2BReservationHistory pattern.
              const productIds = Array.from(
                new Set(snapshotCart.map(c => c.product_id).filter(Boolean) as string[])
              );
              const [productsRes, colorsRes, sizesRes] = await Promise.all([
                productIds.length > 0
                  ? supabase.from('products')
                      .select('id, name, sku, color_id, size_id')
                      .in('id', productIds)
                  : Promise.resolve({ data: [], error: null } as any),
                supabase.from('product_colors').select('id, name').eq('admin_id', buyer.admin_id),
                supabase.from('product_sizes').select('id, name').eq('admin_id', buyer.admin_id),
              ]);
              const productMap: Record<string, any> = {};
              (productsRes.data || []).forEach((p: any) => { productMap[p.id] = p; });
              const colorMap: Record<string, string> = {};
              (colorsRes.data || []).forEach((c: any) => { colorMap[c.id] = c.name; });
              const sizeMap: Record<string, string> = {};
              (sizesRes.data || []).forEach((s: any) => { sizeMap[s.id] = s.name; });

              const enrichedItems = (proFormaResult.invoice.invoice_items || []).map((it: any, idx: number) => {
                const cartLine = snapshotCart[idx];
                const p = cartLine?.product_id ? productMap[cartLine.product_id] : null;
                return {
                  ...it,
                  product_name:
                    (p?.name && p.name.trim()) ||
                    it.product_name ||
                    (it.description ? String(it.description).split(' \u2014 ')[0] : '') ||
                    'Item',
                  sku: p?.sku || it.sku || '',
                  color_name: p?.color_id ? (colorMap[p.color_id] || it.color_name || '') : (it.color_name || ''),
                  size_name: p?.size_id ? (sizeMap[p.size_id] || it.size_name || '') : (it.size_name || ''),
                };
              });
              const enrichedInvoice = {
                ...proFormaResult.invoice,
                invoice_items: enrichedItems,
              };

              const epcQr = await buildEpcQrDataUrl(supplier, enrichedInvoice.total_amount, enrichedInvoice.invoice_number);
              const invoiceHtml = generateProFormaInvoiceHTML(enrichedInvoice, buyer, supplier, epcQr);
              const invPdf = await htmlToPdfBase64(invoiceHtml, { scale: 1.5, jpegQuality: 0.85 });

              // Upload the PDF to Supabase Storage instead of inlining it in
              // the email JSON body. Binary upload is ~30% smaller than the
              // base64 form and goes via storage's own pipeline (no 60s ACK
              // timeout). The edge function then fetches the file
              // server-to-server using its service role.
              const filename = `Invoice-${enrichedInvoice.invoice_number}.pdf`;
              const objectKey = `${buyer.admin_id}/${enrichedInvoice.invoice_number}.pdf`;
              const pdfBytes = Uint8Array.from(atob(invPdf), c => c.charCodeAt(0));
              const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

              const upload = await supabase.storage
                .from('invoice-attachments')
                .upload(objectKey, pdfBlob, {
                  contentType: 'application/pdf',
                  upsert: true,
                });

              if (upload.error) {
                // Fall back to inline base64 if storage upload fails so we
                // at least try to send (still subject to JSON-payload
                // upload limits, but better than no email at all).
                console.error('Failed to upload invoice PDF to storage:', upload.error);
                attachments.push({
                  filename,
                  content: invPdf,
                  contentType: 'application/pdf',
                  encoding: 'base64',
                });
              } else {
                attachments.push({
                  filename,
                  storagePath: `invoice-attachments/${objectKey}`,
                  contentType: 'application/pdf',
                });
              }
            } else if (!proFormaResult.success) {
              setBgError('Invoice could not be created. Your reservation is saved \u2014 please check Invoice History.');
            }
          } catch (pfErr) {
            console.error('Failed to create pro-forma invoice:', pfErr);
            setBgError('Invoice could not be created. Your reservation is saved \u2014 please check Invoice History.');
          }

          try {
            await sendB2BReservationConfirmation(
              buyer,
              reservation,
              snapshotCart,
              {
                company_name: supplier.company_name,
                company_address: supplier.company_address,
                company_phone: supplier.company_phone,
                company_email: supplier.company_email,
                vat_number: supplier.vat_number,
              },
              snapshotPickupDate,
              attachments.length > 0 ? attachments : undefined,
              reservation.id,
              pdfCcEmails.length > 0 ? pdfCcEmails : undefined,
            );
          } catch (emailErr) {
            console.error('Failed to send confirmation email:', emailErr);
            setBgError('Confirmation email could not be sent. Your reservation is saved \u2014 the invoice is downloadable from the Invoices tab.');
          }
        } catch (bgErr) {
          console.error('Background reservation post-processing failed:', bgErr);
        }
      })();
    } catch (err: any) {
      alert(err.message || 'Reservation failed.');
      setReserving(false);
    }
  };

  const { user } = useAuth();

  // Admin viewing the B2B portal - show admin overview
  if (!buyer && user) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Store className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">B2B Portal</h1>
            <p className="text-gray-600 dark:text-gray-400">This is the buyer-facing portal your B2B clients use to browse products and place reservations.</p>
          </div>
        </div>
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">How it works</h2>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2"><span className="font-semibold text-blue-600 mt-0.5">1.</span> Create a B2B buyer account in <a href="/admin/users" className="text-blue-600 hover:underline font-medium">User Management</a> (B2B Buyers tab)</li>
            <li className="flex items-start gap-2"><span className="font-semibold text-blue-600 mt-0.5">2.</span> The buyer logs in with their credentials and sees this portal</li>
            <li className="flex items-start gap-2"><span className="font-semibold text-blue-600 mt-0.5">3.</span> They browse your products with their account discount applied</li>
            <li className="flex items-start gap-2"><span className="font-semibold text-blue-600 mt-0.5">4.</span> They reserve items and choose a pickup date (min 3 days out)</li>
            <li className="flex items-start gap-2"><span className="font-semibold text-blue-600 mt-0.5">5.</span> You confirm and mark ready in <a href="/reports" className="text-blue-600 hover:underline font-medium">Reports &gt; Reservations</a> (B2B reservations show a badge)</li>
            <li className="flex items-start gap-2"><span className="font-semibold text-blue-600 mt-0.5">6.</span> The buyer gets an email notification when the order is ready for pickup</li>
          </ul>
        </div>
        <B2BAdminInvoiceManager />
      </div>
    );
  }

  if (!buyer) return null;

  // Shared cart panel content (used in both desktop sidebar and mobile drawer)
  const cartPanelContent = (
    <>
      {cart.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ShoppingCart className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Your cart is empty</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add products to get started</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {cart.map(c => (
              <div key={c.id} className="flex gap-3 pb-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">{c.name}</p>
                  {(c.color_name || c.size_name) && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                      {[c.color_name, c.size_name].filter(Boolean).join(' / ')}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatPrice(c.discounted_price)} each</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateCartQty(c.id, c.cartQuantity - 1)}
                      className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-bold text-gray-900 dark:text-gray-100">{c.cartQuantity}</span>
                    <button
                      onClick={() => updateCartQty(c.id, c.cartQuantity + 1)}
                      disabled={c.cartQuantity >= c.available_quantity}
                      className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatPrice(c.lineTotal)}</p>
                  <button
                    onClick={() => removeFromCart(c.id)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>Subtotal excl. BTW</span>
              <span className="font-medium">{formatPrice(cartTotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>{vatPct}% BTW</span>
              <span className="font-medium">{formatPrice(cartTaxAmount)}</span>
            </div>
            <div className="flex justify-between text-base font-bold text-gray-900 dark:text-gray-100 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span>Total incl. BTW</span>
              <span className="text-blue-600 dark:text-blue-400">{formatPrice(cartTotalIncVat)}</span>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="h-4 w-4 inline mr-1.5" />Pickup Date
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="date"
              value={pickupDate}
              onChange={e => setPickupDate(e.target.value)}
              min={minPickupDate}
              className="input w-full text-base"
              required
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Minimum 3 days from today</p>
          </div>

          <Button
            onClick={handleReserve}
            disabled={reserving || cart.length === 0 || !pickupDate}
            className="w-full mt-4 h-12 text-base font-semibold"
          >
            {reserving ? 'Reserving...' : `Reserve — ${formatPrice(cartTotalIncVat)}`}
          </Button>
        </>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* ── Top Bar ── */}
      <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          {/* Brand */}
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Store className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900 dark:text-gray-100 text-base">B2B Portal</span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {(['shop', 'history', 'invoices', 'settings'] as ActiveView[]).map(view => (
              <button
                key={view}
                onClick={() => setActiveView(view)}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors capitalize flex items-center gap-1.5 ${activeView === view ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                {view === 'shop' && <Package className="h-4 w-4" />}
                {view === 'history' && <History className="h-4 w-4" />}
                {view === 'invoices' && <FileText className="h-4 w-4" />}
                {view === 'settings' && <Settings className="h-4 w-4" />}
                {view.charAt(0).toUpperCase() + view.slice(1)}
              </button>
            ))}
          </nav>

          {/* Desktop right actions */}
          <div className="hidden lg:flex items-center gap-2">
            {activeView === 'shop' && (
              <button
                onClick={() => setShowCart(!showCart)}
                className="relative flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                <ShoppingCart className="h-4 w-4" />
                Cart
                {cartItemsCount > 0 && (
                  <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
                    {cartItemsCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>

          {/* Mobile right actions */}
          <div className="flex lg:hidden items-center gap-2">
            {activeView === 'shop' && (
              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                aria-label="Open cart"
              >
                <ShoppingCart className="h-5 w-5" />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
                    {cartItemsCount > 9 ? '9+' : cartItemsCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 space-y-1 shadow-lg">
            {(['shop', 'history', 'invoices', 'settings'] as ActiveView[]).map(view => (
              <button
                key={view}
                onClick={() => { setActiveView(view); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeView === view ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                {view === 'shop' && <Package className="h-5 w-5" />}
                {view === 'history' && <History className="h-5 w-5" />}
                {view === 'invoices' && <FileText className="h-5 w-5" />}
                {view === 'settings' && <Settings className="h-5 w-5" />}
                <span className="capitalize">{view}</span>
              </button>
            ))}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-1 mt-1">
              <button
                onClick={() => { logout(); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <LogOut className="h-5 w-5" /> Logout
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Mobile cart drawer overlay ── */}
      {showCart && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCart(false)}
          />
          {/* Drawer */}
          <div className="relative ml-auto w-full max-w-sm h-full bg-white dark:bg-gray-800 flex flex-col shadow-2xl">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
                <h2 className="font-bold text-gray-900 dark:text-gray-100 text-lg">Cart</h2>
                {cartItemsCount > 0 && (
                  <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                    {cartItemsCount} {cartItemsCount === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Drawer body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {cartPanelContent}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        <BuyerProfileSummary buyer={buyer} />

        {activeView === 'shop' && (
          <div className="flex gap-6">
            {/* Product Grid */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Search & Filters */}
              <div className="card p-3 sm:p-4">
                {/* Search row */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="input pl-10 w-full h-11"
                  />
                </div>
                {/* Browse view toggle (grouped vs flat) + density (grouped only) */}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setBrowseView(browseView === 'grouped' ? 'all' : 'grouped')}
                    aria-pressed={browseView === 'all'}
                    title={browseView === 'grouped' ? 'Show all products' : 'Group by product'}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                      browseView === 'all'
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                        : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    {browseView === 'grouped' ? <LayoutGrid className="h-4 w-4" /> : <Rows3 className="h-4 w-4" />}
                    <span className="hidden sm:inline">{browseView === 'grouped' ? 'Grouped' : 'All products'}</span>
                  </button>
                  {browseView === 'grouped' && (
                    <SegmentedControl<ProductGroupDensity>
                      value={browseDensity}
                      onChange={setBrowseDensity}
                      ariaLabel="View density"
                      options={[
                        { value: 'list', label: '', icon: <List className="h-4 w-4" /> },
                        { value: 'grid', label: '', icon: <LayoutGrid className="h-4 w-4" /> },
                      ]}
                    />
                  )}
                </div>
                {/* Filter toggle (mobile) / filters row (desktop) */}
                <div className="mt-3">
                  {/* Mobile: toggle button */}
                  <button
                    className="lg:hidden w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      Filters
                      {(categoryFilter || colorFilter || sizeFilter) && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                  </button>
                  {/* Desktop filters always visible */}
                  <div className={`${showFilters ? 'flex' : 'hidden'} lg:flex flex-col sm:flex-row flex-wrap gap-2 mt-2 lg:mt-0`}>
                    <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="input flex-1 min-w-0 sm:min-w-[140px] h-10 text-sm">
                      <option value="">All Categories</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {colors.length > 0 && (
                      <select value={colorFilter} onChange={e => setColorFilter(e.target.value)} className="input flex-1 min-w-0 sm:min-w-[120px] h-10 text-sm">
                        <option value="">All Colors</option>
                        {colors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    )}
                    {sizes.length > 0 && (
                      <select value={sizeFilter} onChange={e => setSizeFilter(e.target.value)} className="input flex-1 min-w-0 sm:min-w-[120px] h-10 text-sm">
                        <option value="">All Sizes</option>
                        {sizes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {allItemsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="card p-12 text-center">
                  <Package className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">No products found</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your filters</p>
                </div>
              ) : browseView === 'all' ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {filteredProducts.map(item => {
                    const available = getAvailableQty(item.id);
                    const discountedPrice = (item.sell_price || 0) / discount;
                    const inCart = cart.find(c => c.id === item.id);
                    const outOfStock = available <= 0;

                    return (
                      <div key={item.id} className={`card overflow-hidden hover:shadow-md transition-shadow flex flex-col ${outOfStock ? 'opacity-70' : ''}`}>
                        {/* Product image */}
                        <div className={`bg-gray-100 dark:bg-gray-700 overflow-hidden ${item.image_url ? 'h-36 sm:h-40' : 'h-0'}`}>
                          {item.image_url && (
                            <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" loading="lazy" />
                          )}
                        </div>
                        <div className="p-3 flex flex-col flex-1">
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 leading-tight line-clamp-2">{item.name}</h4>
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            {item.sku && <span className="text-xs font-mono text-gray-400 dark:text-gray-500">{item.sku}</span>}
                            {getColorName(item.color_id) && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{getColorName(item.color_id)}</span>
                            )}
                            {getSizeName(item.size_id) && (
                              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded font-medium">{getSizeName(item.size_id)}</span>
                            )}
                          </div>
                          <div className="mt-2 flex items-baseline gap-1.5">
                            <span className="text-base sm:text-lg font-bold text-blue-600 dark:text-blue-400">{formatPrice(discountedPrice)}</span>
                            {discount > 1 && (
                              <span className="text-xs text-gray-400 line-through">{formatPrice(item.sell_price || 0)}</span>
                            )}
                          </div>
                          <div className="mt-auto pt-2 flex items-center justify-between">
                            {outOfStock ? (
                              <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                Out of stock
                              </span>
                            ) : (
                              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${available <= 5 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}`}>
                                {available} left
                              </span>
                            )}
                            {outOfStock ? (
                              <button
                                disabled
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-semibold rounded-lg cursor-not-allowed"
                                title="Currently out of stock"
                              >
                                Unavailable
                              </button>
                            ) : inCart ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => updateCartQty(item.id, inCart.cartQuantity - 1)}
                                  className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span className="w-7 text-center text-sm font-bold text-gray-900 dark:text-gray-100">{inCart.cartQuantity}</span>
                                <button
                                  onClick={() => updateCartQty(item.id, inCart.cartQuantity + 1)}
                                  disabled={inCart.cartQuantity >= available}
                                  className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => addToCart(item)}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
                              >
                                <Plus className="h-3.5 w-3.5" /> Add
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : browseDensity === 'list' ? (
                <div className="space-y-2">
                  {productGroups.map(group => (
                    <ProductGroupCard
                      key={group.productName}
                      group={group}
                      density="list"
                      showPricing
                      onSelect={setSelectedProductGroup}
                      colorSwatches={colorsForGrouping}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {productGroups.map(group => (
                    <ProductGroupCard
                      key={group.productName}
                      group={group}
                      density="grid"
                      showPricing
                      onSelect={setSelectedProductGroup}
                      colorSwatches={colorsForGrouping}
                    />
                  ))}
                </div>
              )}

              {/* Bottom padding on mobile so FAB doesn't cover last product */}
              <div className="h-4 lg:hidden" />
            </div>

            {/* Cart Sidebar (desktop only) */}
            {showCart && (
              <div className="hidden lg:block w-80 flex-shrink-0">
                <div className="card p-4 sticky top-4 space-y-4 max-h-[calc(100vh-100px)] overflow-y-auto">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-blue-600" />
                      <h3 className="font-bold text-gray-900 dark:text-gray-100">Cart</h3>
                      {cartItemsCount > 0 && (
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                          {cartItemsCount}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setShowCart(false)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {cartPanelContent}
                </div>
              </div>
            )}
          </div>
        )}

        {activeView === 'history' && <B2BReservationHistory />}

        {activeView === 'invoices' && <B2BInvoiceHistory />}

        {activeView === 'settings' && (
          <div className="space-y-6">
            <B2BPasswordChange />
            <div className="card p-5 sm:p-6 max-w-lg">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Company Profile</h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: 'Legal Name', value: buyer.legal_entity_name },
                  { label: 'Trade Name', value: buyer.trade_name },
                  buyer.vat_number && { label: 'VAT Number', value: buyer.vat_number },
                  buyer.billing_address && { label: 'Billing Address', value: `${buyer.billing_address}, ${buyer.billing_city} ${buyer.billing_postal_code}` },
                  buyer.shipping_address && { label: 'Shipping Address', value: `${buyer.shipping_address}, ${buyer.shipping_city} ${buyer.shipping_postal_code}` },
                  buyer.payment_terms && { label: 'Payment Terms', value: buyer.payment_terms },
                  { label: 'Discount Factor', value: `÷ ${buyer.account_discount}` },
                ].filter(Boolean).map((row: any) => (
                  <div key={row.label} className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-4 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide font-semibold">{row.label}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 sm:text-right">{row.value}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">Contact your supplier to update company details.</p>
            </div>
          </div>
        )}
      </div>

      {/* Variant matrix modal (grouped view) */}
      {selectedProductGroup && (
        <ProductVariantMatrixModal
          group={selectedProductGroup}
          colors={colorsForGrouping}
          sizes={sizesForGrouping}
          showPricing
          showOutOfStock
          onClose={() => setSelectedProductGroup(null)}
          onAddVariants={addVariantsToCart}
          getLocationQty={getWarehouseQty}
          getAvailableQty={getMatrixAvailableQty}
          getReservedQty={getReservedQuantityForProduct}
        />
      )}

      {/* Confirmation Modal */}
      {confirmationData && (
        <B2BReservationConfirmation
          reservation={confirmationData.reservation}
          pickupDate={confirmationData.pickupDate}
          bgError={bgError}
          onClose={() => {
            setConfirmationData(null);
            setBgError(null);
          }}
        />
      )}
    </div>
  );
};
